import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";

/* ─────────────────────────────────────────
   HERO SUB-COMPONENTS
───────────────────────────────────────── */

/* Animated keyword typer */
function KeywordTyper({ keywords }) {
  const [idx, setIdx] = useState(0);
  const [text, setText] = useState("");
  const [del, setDel] = useState(false);
  const [ci, setCi] = useState(0);
  useEffect(() => {
    const w = keywords[idx];
    if (!del && ci < w.length) { const t = setTimeout(() => setCi(c => c + 1), 65); return () => clearTimeout(t); }
    if (!del && ci === w.length) { const t = setTimeout(() => setDel(true), 1800); return () => clearTimeout(t); }
    if (del && ci > 0) { const t = setTimeout(() => setCi(c => c - 1), 38); return () => clearTimeout(t); }
    if (del && ci === 0) { setDel(false); setIdx(i => (i + 1) % keywords.length); }
  }, [ci, del, idx, keywords]);
  useEffect(() => setText(keywords[idx].slice(0, ci)), [ci, idx, keywords]);
  return (
    <span style={{ fontSize: 12, color: "#94a3b8" }}>
      {text}
      <span style={{ animation: "seoBlink 1s step-end infinite", color: "#818cf8" }}>|</span>
    </span>
  );
}

/* Sparkline canvas */
function Sparkline({ data, color, height = 30 }) {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    c.width = c.offsetWidth * dpr;
    c.height = height * dpr;
    ctx.scale(dpr, dpr);
    const W = c.offsetWidth, H = height;
    const min = Math.min(...data), max = Math.max(...data), range = max - min || 1;
    const pts = data.map((v, i) => [i / (data.length - 1) * W, H - ((v - min) / range) * (H - 5) - 3]);
    const g = ctx.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, color + "40"); g.addColorStop(1, color + "00");
    ctx.beginPath(); ctx.moveTo(pts[0][0], H);
    pts.forEach(([x, y]) => ctx.lineTo(x, y));
    ctx.lineTo(W, H); ctx.closePath(); ctx.fillStyle = g; ctx.fill();
    ctx.beginPath(); ctx.moveTo(pts[0][0], pts[0][1]);
    pts.forEach(([x, y]) => ctx.lineTo(x, y));
    ctx.strokeStyle = color; ctx.lineWidth = 2; ctx.lineJoin = "round"; ctx.stroke();
    const [lx, ly] = pts[pts.length - 1];
    ctx.beginPath(); ctx.arc(lx, ly, 3.5, 0, Math.PI * 2);
    ctx.fillStyle = color; ctx.fill();
  }, [data, color, height]);
  return <canvas ref={ref} style={{ width: "100%", height, display: "block" }} />;
}

/* Particle canvas */
function HeroParticles() {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext("2d");
    const resize = () => { c.width = c.offsetWidth; c.height = c.offsetHeight; };
    resize(); window.addEventListener("resize", resize);
    const pts = Array.from({ length: 40 }, () => ({
      x: Math.random() * c.width, y: Math.random() * c.height,
      r: Math.random() * 1.1 + .3,
      vx: (Math.random() - .5) * .18, vy: (Math.random() - .5) * .18,
      o: Math.random() * .2 + .05,
    }));
    let raf;
    const loop = () => {
      ctx.clearRect(0, 0, c.width, c.height);
      pts.forEach(p => {
        p.x = (p.x + p.vx + c.width) % c.width;
        p.y = (p.y + p.vy + c.height) % c.height;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(74,95,212,${p.o})`; ctx.fill();
      });
      raf = requestAnimationFrame(loop);
    };
    loop();
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);
  return (
    <canvas ref={ref} style={{
      position: "absolute", inset: 0, width: "100%", height: "100%",
      pointerEvents: "none", zIndex: 0,
    }} />
  );
}

/* ─────────────────────────────────────────
   MAIN EXPORT
───────────────────────────────────────── */
export default function SEOPage() {

  /* live visitor ticker */
  const [visitors, setVisitors] = useState(49078);
  useEffect(() => {
    const t = setInterval(() => setVisitors(n => n + Math.floor(Math.random() * 3)), 2200);
    return () => clearInterval(t);
  }, []);

  const services = [
    { title: "Technical SEO",    desc: "Core Web Vitals, crawl optimization, schema markup, indexing fixes, and scalable site architecture." },
    { title: "Content SEO",      desc: "Semantic content systems engineered for topical authority and long-term ranking growth." },
    { title: "Local SEO",        desc: "Google Business Profile optimization, maps ranking, local citations, and geo-targeted visibility." },
    { title: "Link Building",    desc: "Authority backlink acquisition through high-quality outreach and niche-relevant placements." },
    { title: "Enterprise SEO",   desc: "Scalable SEO systems for SaaS platforms, software companies, ERP businesses, and startups." },
    { title: "SEO Audits",       desc: "Complete SEO analysis covering performance, content gaps, ranking opportunities, and technical issues." },
  ];

  const industries = [
    "SEO for SaaS Companies","SEO for ERP Software","SEO for eCommerce",
    "SEO for Manufacturing","SEO for Rice Mills","SEO for Schools",
    "SEO for Healthcare","SEO for Startups",
  ];

  const process = [
    "SEO Audit & Technical Analysis","Keyword & Competitor Research",
    "Content Strategy Development","On-Page SEO Optimization",
    "Authority Link Building","Performance Monitoring & Scaling",
  ];

  const faqs = [
    { q: "How long does SEO take?",              a: "Most businesses begin seeing measurable ranking improvements within 3 to 6 months depending on competition and authority." },
    { q: "Do backlinks still matter in SEO?",    a: "Yes. High-quality backlinks remain one of the strongest ranking signals for competitive keywords." },
    { q: "Do you provide local SEO services?",   a: "Yes. We optimize Google Business Profiles, local landing pages, citations, and local search visibility." },
    { q: "What industries do you work with?",    a: "We work with SaaS companies, ERP software providers, manufacturers, eCommerce stores, startups, and local businesses." },
  ];

  /* keyword rankings data */
  const kwData = [
    { rank: 1, kw: "mobile app development company", vol: "8.5k/mo", color: "#4ade80", spark: [18,14,10,6,3,1,1] },
    { rank: 1, kw: "shopify development",             vol: "5.5k/mo", color: "#4ade80", spark: [20,15,9,5,2,1,1] },
    { rank: 2, kw: "react native agency",             vol: "3.3k/mo", color: "#60a5fa", spark: [22,17,12,7,4,2,2] },
    { rank: 3, kw: "ecommerce development",           vol: "5.4k/mo", color: "#60a5fa", spark: [25,19,14,9,6,4,3] },
    { rank: 2, kw: "flutter app developers",          vol: "2.3k/mo", color: "#60a5fa", spark: [21,16,11,7,4,2,2] },
    { rank: 4, kw: "nodejs backend services",         vol: "1.9k/mo", color: "#facc15", spark: [28,22,17,12,8,5,4] },
  ];

  const serpResults = [
    { pos: 1, url: "softcoders.com",  title: "SoftCoders — #1 Mobile App Development",  desc: "Award-winning iOS & Android development. 200+ apps...", top: true  },
    { pos: 2, url: "techmakers.io",   title: "Top App Developers 2026 | TechMakers",     desc: "Cross-platform and native mobile solutions for startups.", top: false },
    { pos: 3, url: "appforge.dev",    title: "Mobile App Agency — AppForge",             desc: "React Native & Flutter specialists. Fast delivery.",      top: false },
  ];

  return (
    <div className="bg-[#070B1A] text-white overflow-hidden">

      
  <Helmet>

    {/* PRIMARY SEO */}
    <title>
      SEO Services Company | Technical SEO & Local SEO Agency | SoftCoders
    </title>

    <meta
      name="description"
      content="SoftCoders is an SEO services company providing technical SEO, local SEO, enterprise SEO, link building, content SEO, SEO audits, semantic SEO, keyword ranking strategies, and scalable organic growth solutions for businesses."
    />

    <meta
      name="keywords"
      content="
      seo company,
      seo services company,
      technical seo services,
      local seo company,
      enterprise seo agency,
      seo experts india,
      seo optimization services,
      google ranking services,
      page 1 ranking seo,
      content seo services,
      semantic seo agency,
      ecommerce seo services,
      saas seo agency,
      seo for software companies,
      seo audit services,
      backlink building services,
      organic traffic growth,
      keyword ranking services,
      google search optimization,
      on page seo,
      off page seo,
      website seo optimization,
      local business seo,
      seo consultant india,
      digital marketing seo,
      search engine optimization company,
      seo agency india,
      advanced seo strategies,
      technical seo audit,
      enterprise search optimization
      "
    />

    <meta name="author" content="SoftCoders" />
    <meta name="robots" content="index, follow" />
    <meta name="language" content="English" />
    <meta name="revisit-after" content="2 days" />

    {/* CANONICAL */}
    <link
      rel="canonical"
      href="https://softcoders.in/seo-services"
    />

    {/* OPEN GRAPH */}
    <meta
      property="og:title"
      content="SEO Services Company | SoftCoders"
    />

    <meta
      property="og:description"
      content="Technical SEO, local SEO, enterprise SEO, content optimization, and scalable organic growth services for modern businesses."
    />

    <meta
      property="og:url"
      content="https://softcoders.in/seo-services"
    />

    <meta property="og:type" content="website" />

    <meta
      property="og:image"
      content="https://softcoders.in/og-seo-services.jpg"
    />

    {/* TWITTER SEO */}
    <meta name="twitter:card" content="summary_large_image" />

    <meta
      name="twitter:title"
      content="SEO Services Company | SoftCoders"
    />

    <meta
      name="twitter:description"
      content="Professional SEO services including technical SEO, content SEO, local SEO, and enterprise SEO solutions."
    />

    <meta
      name="twitter:image"
      content="https://softcoders.in/og-seo-services.jpg"
    />

    {/* GEO */}
    <meta name="geo.region" content="IN" />
    <meta name="geo.country" content="India" />

    {/* MOBILE */}
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <meta name="theme-color" content="#070B1A" />

    {/* SCHEMA */}
    <script type="application/ld+json">
      {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "ProfessionalService",
        name: "SoftCoders",
        url: "https://softcoders.in",
        logo: "https://softcoders.in/logo.png",
        image: "https://softcoders.in/og-seo-services.jpg",
        description:
          "SoftCoders provides technical SEO, local SEO, enterprise SEO, content optimization, and scalable organic growth services.",
        address: {
          "@type": "PostalAddress",
          addressCountry: "India"
        },
        areaServed: {
          "@type": "Country",
          name: "India"
        },
        serviceType: [
          "Technical SEO",
          "Local SEO",
          "Enterprise SEO",
          "Content SEO",
          "SEO Audit",
          "Link Building"
        ],
        sameAs: [
          "https://www.facebook.com/",
          "https://www.instagram.com/",
          "https://www.linkedin.com/"
        ]
      })}
    </script>

    {/* FAQ SCHEMA */}
    <script type="application/ld+json">
      {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "How long does SEO take?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Most businesses begin seeing measurable SEO improvements within 3 to 6 months depending on competition and authority."
            }
          },
          {
            "@type": "Question",
            name: "Do backlinks still matter in SEO?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes. High-quality backlinks remain one of the strongest ranking signals for competitive keywords."
            }
          },
          {
            "@type": "Question",
            name: "Do you provide local SEO services?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes. SoftCoders provides Google Business Profile optimization, maps SEO, citations, and local search optimization."
            }
          },
          {
            "@type": "Question",
            name: "What industries do you work with?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "We work with SaaS companies, ERP software providers, manufacturers, eCommerce brands, startups, and local businesses."
            }
          }
        ]
      })}
    </script>

  </Helmet>

      {/* ── GLOBAL ANIMATION STYLES ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800;900&family=Space+Mono:wght@400;700&display=swap');

        @keyframes seoBlink   { 0%,100%{opacity:1} 50%{opacity:0} }
        @keyframes seoFadeUp  { from{opacity:0;transform:translateY(30px)} to{opacity:1;transform:none} }
        @keyframes seoFloat   { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-14px)} }
        @keyframes seoFloatR  { 0%,100%{transform:translateY(0) rotate(0deg)} 45%{transform:translateY(-16px) rotate(.6deg)} }
        @keyframes seoPing    { 0%{transform:scale(1);opacity:.7} 100%{transform:scale(2.5);opacity:0} }
        @keyframes seoPulse   { 0%,100%{opacity:1} 50%{opacity:.3} }
        @keyframes seoMorph   { 0%,100%{background-position:0 50%} 50%{background-position:100% 50%} }
        @keyframes seoSpin    { from{transform:rotate(0)} to{transform:rotate(360deg)} }
        @keyframes seoSpinR   { from{transform:rotate(0)} to{transform:rotate(-360deg)} }
        @keyframes seoShimmer { 0%{background-position:-200% center} 100%{background-position:200% center} }
        @keyframes seoSlide   { from{background-position:-200% center} to{background-position:200% center} }
        @keyframes fadeUp     { from{opacity:0;transform:translateY(40px)} to{opacity:1;transform:translateY(0)} }
        @keyframes floatSlow  { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-12px)} }
        @keyframes pulseGlow  { 0%,100%{opacity:.35} 50%{opacity:.55} }

        .fade-up   { animation: fadeUp 1s ease forwards; }
        .float-slow{ animation: floatSlow 7s ease-in-out infinite; }
        .glow-pulse{ animation: pulseGlow 6s ease-in-out infinite; }

        .seo-shimmer-text {
          background: linear-gradient(90deg,#818cf8,#6b5ce7,#4f8ef7,#818cf8);
          background-size: 250% auto;
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          animation: seoShimmer 3.5s linear infinite;
        }
        .seo-outline-text {
          -webkit-text-stroke: 2px #4a5fd4;
          color: transparent;
        }
        .seo-morph-btn {
          background: linear-gradient(135deg,#1a2472,#2d3fb5,#4a5fd4,#1a2472) !important;
          background-size: 300% 300% !important;
          animation: seoMorph 4s ease infinite !important;
        }
        .seo-pill {
          padding: 7px 16px;
          border-radius: 999px;
          font-size: 13px;
          font-weight: 600;
          border: 1px solid rgba(74,95,212,.28);
          color: #8fa3f8;
          background: rgba(74,95,212,.09);
          cursor: default;
          transition: all .3s ease;
        }
        .seo-pill:hover {
          border-color: rgba(74,95,212,.6);
          background: rgba(74,95,212,.18);
          color: #c7d2fe;
        }

        /* hero responsive */
        .seo-hero-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 40px;
          align-items: center;
        }
        .seo-right-stack {
          position: relative;
          height: 540px;
        }
        @media (max-width: 1024px) {
          .seo-hero-grid { grid-template-columns: 1fr; }
          .seo-right-stack { height: auto; min-height: 500px; }
          .seo-badge-rank, .seo-badge-traffic, .seo-badge-kw { display: none !important; }
        }
        @media (max-width: 640px) {
          .seo-hero-grid { gap: 24px; }
          .seo-pills-row { display: none !important; }
        }
      `}</style>

      {/* ════════════════════════════════════════
          HERO SECTION  — completely rebuilt
      ════════════════════════════════════════ */}
      <section style={{
        position: "relative", overflow: "hidden",
        background: "linear-gradient(180deg,#080d22 0%,#070b1a 100%)",
        paddingBottom: 56,
      }}>
        <HeroParticles />

        {/* bg decoration */}
        <div style={{ position: "absolute", inset: 0, zIndex: 0, pointerEvents: "none" }}>
          {/* grid */}
          <div style={{
            position: "absolute", inset: 0, opacity: .028,
            backgroundImage: "linear-gradient(rgba(74,95,212,.5) 1px,transparent 1px),linear-gradient(90deg,rgba(74,95,212,.5) 1px,transparent 1px)",
            backgroundSize: "72px 72px",
          }} />
          {/* diagonal right tint */}
          <div style={{
            position: "absolute", top: 0, right: 0, width: "50%", height: "100%",
            background: "linear-gradient(140deg,rgba(26,36,114,.35),rgba(45,63,181,.15))",
            clipPath: "polygon(14% 0,100% 0,100% 100%,0% 100%)",
          }} />
          {/* glow orbs */}
          <div style={{ position: "absolute", top: "-10%", right: "5%", width: 520, height: 520, borderRadius: "50%", opacity: .13, background: "radial-gradient(circle,#2d3fb5,transparent 70%)", animation: "seoFloatR 9s ease-in-out infinite" }} />
          <div style={{ position: "absolute", bottom: "-5%", left: "3%",  width: 380, height: 380, borderRadius: "50%", opacity: .09, background: "radial-gradient(circle,#4a5fd4,transparent 70%)", animation: "seoFloat 8s ease-in-out infinite" }} />
          {/* spinning rings */}
          <div style={{ position: "absolute", top: "50%", right: "4%", width: 260, height: 260, borderRadius: "50%", border: "1px solid rgba(74,95,212,.09)", transform: "translateY(-50%)", animation: "seoSpin 42s linear infinite" }} />
          <div style={{ position: "absolute", top: "50%", right: "4%", width: 200, height: 200, borderRadius: "50%", border: "1px dashed rgba(74,95,212,.06)", transform: "translate(30px,-50%)", animation: "seoSpinR 28s linear infinite" }} />
          {/* ghost SEO text */}
          <div style={{ position: "absolute", bottom: -24, right: -12, fontSize: "clamp(80px,14vw,200px)", fontWeight: 900, color: "rgba(45,63,181,.04)", userSelect: "none", lineHeight: 1, fontFamily: "'Outfit',sans-serif", letterSpacing: "-4px" }}>SEO</div>
        </div>

        <div style={{ maxWidth: 1240, margin: "0 auto", padding: "80px 48px 0", position: "relative", zIndex: 10 }}>

          {/* top bar */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 40 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ position: "relative" }}>
                <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#8fa3f8", animation: "seoPulse 2s infinite" }} />
                <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: "#8fa3f8", animation: "seoPing 1.5s ease infinite" }} />
              </div>
              <span style={{ color: "#8fa3f8", fontSize: 11, letterSpacing: ".22em", textTransform: "uppercase", fontFamily: "'Space Mono',monospace" }}>
                SoftCoders / SEO Division
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: "rgba(255,255,255,.18)", letterSpacing: ".12em", textTransform: "uppercase" }}>
              <span style={{ color: "#4ade80" }}>✓</span>
              <span>Technical · On-Page · Off-Page</span>
            </div>
          </div>

          {/* hero grid */}
          <div className="seo-hero-grid">

            {/* ── LEFT COLUMN ── */}
            <div className="fade-up">

              {/* eyebrow */}
              <div style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                border: "1px solid rgba(74,95,212,.3)", background: "rgba(74,95,212,.1)",
                padding: "7px 18px", borderRadius: 999, fontSize: 13,
                color: "#a5b4fc", marginBottom: 28, backdropFilter: "blur(10px)",
              }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#818cf8", animation: "seoPulse 2s infinite" }} />
                Google Page 1 Specialists
              </div>

              {/* HEADLINE — matches image exactly */}
              <h1 style={{ fontFamily: "'Outfit',sans-serif", lineHeight: .92, letterSpacing: "-2px", margin: 0 }}>
                <span style={{ display: "block", fontSize: "clamp(36px,5vw,68px)", fontWeight: 900, color: "#fff", marginBottom: 4 }}>
                  SEO Services
                </span>
                <span className="seo-outline-text" style={{ display: "block", fontSize: "clamp(34px,4.8vw,66px)", fontWeight: 900, marginBottom: 4, letterSpacing: "-1.5px" }}>
                  That Rank.
                </span>
                <span style={{ display: "block", fontSize: "clamp(34px,4.8vw,66px)", fontWeight: 900 }}>
                  That{" "}
                  <span className="seo-shimmer-text">Convert.</span>
                </span>
              </h1>

              <p style={{ marginTop: 24, color: "#7a8bb5", fontSize: 16, lineHeight: 1.8, maxWidth: 460 }}>
                SoftCoders drives organic growth with data-driven SEO — technical audits,
                content strategy, link building, and local SEO that puts you on Page 1
                and keeps you there.
              </p>

              {/* pills */}
              <div className="seo-pills-row" style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 20 }}>
                {["On-Page SEO","Technical SEO","Link Building","Local SEO","Content SEO"].map((p,i) => (
                  <span key={i} className="seo-pill" style={{ animationDelay: `${.7+i*.08}s`, animation: `seoFadeUp .6s ease ${.7+i*.08}s both` }}>
                    {p}
                  </span>
                ))}
              </div>

              {/* CTA buttons */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginTop: 32 }}>
                <a
    href="https://wa.me/919254147455?text=Hello%20SoftCoders%2C%20I%20want%20to%20discuss%20my%20project."
    target="_blank"
    rel="noopener noreferrer"

                  className="seo-morph-btn"
                  style={{
                    padding: "15px 30px", borderRadius: 14, fontWeight: 800, fontSize: 16,
                    border: "none", color: "#fff", cursor: "pointer",
                    display: "flex", alignItems: "center", gap: 8,
                    boxShadow: "0 14px 44px rgba(45,63,181,.45)",
                    fontFamily: "'Outfit',sans-serif",
                    transition: "transform .3s, box-shadow .3s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.05)"; e.currentTarget.style.boxShadow = "0 20px 60px rgba(45,63,181,.6)"; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "none";         e.currentTarget.style.boxShadow = "0 14px 44px rgba(45,63,181,.45)"; }}
                >
                  Get Free SEO Audit →
                </a>
                <a
    href="/contact"
                  style={{
                    padding: "15px 30px", borderRadius: 14, fontWeight: 600, fontSize: 16,
                    border: "1px solid rgba(74,95,212,.28)", background: "transparent",
                    color: "#7a8bb5", cursor: "pointer",
                    transition: "all .3s ease",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(74,95,212,.6)"; e.currentTarget.style.color = "#fff"; e.currentTarget.style.background = "rgba(74,95,212,.09)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(74,95,212,.28)"; e.currentTarget.style.color = "#7a8bb5"; e.currentTarget.style.background = "transparent"; }}
                >
                  View Results
                </a>
              </div>

              {/* mini stats */}
              <div style={{ display: "flex", gap: 32, marginTop: 44, flexWrap: "wrap" }}>
                {[["#1","Rankings Achieved"],["340%","Avg Traffic Lift"],["847+","Page 1 Keywords"]].map(([v,l],i) => (
                  <div key={i}>
                    <div style={{ fontSize: 22, fontWeight: 900, color: "#8fa3f8", fontFamily: "'Space Mono',monospace" }}>{v}</div>
                    <div style={{ color: "#475569", fontSize: 12, marginTop: 3 }}>{l}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* ── RIGHT COLUMN — overlapping dashboard cards ── */}
            <div className="seo-right-stack">

              {/* ── CARD 1: Keyword Rankings (back, tilted left) ── */}
              <div
                style={{
                  position: "absolute", left: 0, top: 30, width: 224, zIndex: 10,
                  background: "rgba(10,13,34,.97)",
                  border: "1px solid rgba(74,95,212,.22)",
                  borderRadius: 18, overflow: "hidden",
                  boxShadow: "0 24px 60px rgba(0,0,0,.65)",
                  animation: "seoFloatR 9s ease-in-out infinite",
                }}
              >
                {/* header */}
                <div style={{ padding: "12px 14px", borderBottom: "1px solid rgba(255,255,255,.05)", background: "rgba(16,22,56,.6)" }}>
                  <div style={{ fontSize: 10, color: "#4b5563", marginBottom: 2 }}>Keyword Rankings</div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: "#fff", fontFamily: "'Outfit',sans-serif" }}>Top Keywords</div>
                </div>
                {/* rows */}
                <div style={{ padding: "8px 8px" }}>
                  {kwData.map((k, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", borderRadius: 8, marginBottom: 3, background: "rgba(255,255,255,.025)", border: "1px solid rgba(255,255,255,.035)" }}>
                      <div style={{ width: 20, height: 20, borderRadius: "50%", background: k.rank <= 2 ? "#2d3fb5" : "rgba(255,255,255,.07)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 900, color: k.rank <= 2 ? "#fff" : "#64748b", flexShrink: 0, fontFamily: "'Space Mono',monospace" }}>{k.rank}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{k.kw}</div>
                        <div style={{ fontSize: 9, color: "#374151" }}>{k.vol}</div>
                      </div>
                      <div style={{ width: 36, flexShrink: 0 }}>
                        <Sparkline data={k.spark} color={k.color} height={20} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── CARD 2: SERP + Live Visitors (front, main) ── */}
              <div
                style={{
                  position: "absolute", right: 0, top: 0, width: 286, zIndex: 20,
                  background: "rgba(8,11,28,.98)",
                  border: "1px solid rgba(74,95,212,.22)",
                  borderRadius: 18, overflow: "hidden",
                  boxShadow: "0 40px 90px rgba(0,0,0,.75), 0 0 0 1px rgba(74,95,212,.06)",
                  animation: "seoFloat 8s ease-in-out infinite",
                }}
              >
                {/* Google search bar */}
                <div style={{ padding: "12px 14px", borderBottom: "1px solid rgba(255,255,255,.05)", background: "rgba(14,18,50,.65)", display: "flex", alignItems: "center", gap: 8 }}>
                  {/* G logo */}
                  <div style={{ width: 16, height: 16, borderRadius: "50%", background: "conic-gradient(#ea4335 0% 25%,#fbbc05 25% 50%,#34a853 50% 75%,#4285f4 75%)", flexShrink: 0 }} />
                  <div style={{ flex: 1, background: "rgba(255,255,255,.07)", borderRadius: 20, padding: "5px 10px" }}>
                    <KeywordTyper keywords={["mobile app development company","custom software development","saas platform services"]} />
                  </div>
                </div>

                {/* result count */}
                <div style={{ padding: "5px 14px", background: "rgba(8,11,28,.5)", borderBottom: "1px solid rgba(255,255,255,.03)", fontSize: 9, color: "#374151", fontFamily: "'Space Mono',monospace" }}>
                  About 2,140,000,000 results (0.42 sec)
                </div>

                {/* live visitors */}
                <div style={{ margin: "10px 10px 6px", padding: "10px 12px", borderRadius: 10, background: "rgba(18,24,58,.8)", border: "1px solid rgba(74,95,212,.2)" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ fontSize: 10, color: "#64748b" }}>Organic Visitors Today</span>
                    <span style={{ fontSize: 9, color: "#4ade80", fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}>
                      <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#4ade80", display: "inline-block", animation: "seoPulse 1.5s infinite" }} /> LIVE
                    </span>
                  </div>
                  <div style={{ fontSize: 26, fontWeight: 900, color: "#fff", fontFamily: "'Outfit',sans-serif", lineHeight: 1.1 }}>
                    {visitors.toLocaleString()}
                  </div>
                  <div style={{ fontSize: 11, color: "#4ade80", marginTop: 3, fontWeight: 600 }}>↑ 324%</div>
                </div>

                {/* search results label */}
                <div style={{ padding: "2px 14px 5px", fontSize: 10, color: "#4b5563", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".08em" }}>Search Results</div>

                {/* SERP rows */}
                <div style={{ padding: "0 10px 8px" }}>
                  {serpResults.map((r, i) => (
                    <div key={i} style={{ padding: "9px 11px", borderRadius: 8, background: r.top ? "rgba(45,63,181,.1)" : "rgba(255,255,255,.02)", border: `1px solid ${r.top ? "rgba(74,95,212,.3)" : "rgba(255,255,255,.04)"}`, marginBottom: 5, position: "relative", overflow: "hidden" }}>
                      {r.top && <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: "linear-gradient(90deg,#2d3fb5,#4f8ef7)" }} />}
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                        <div style={{ width: 16, height: 16, borderRadius: 4, background: r.top ? "#2d3fb5" : "rgba(255,255,255,.06)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 900, color: r.top ? "#fff" : "#64748b", flexShrink: 0, fontFamily: "'Space Mono',monospace" }}>{r.pos}</div>
                        <span style={{ fontSize: 9, color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{r.url}</span>
                        {r.top && <span style={{ fontSize: 8, padding: "1px 6px", borderRadius: 99, background: "#2d3fb5", color: "#fff", fontWeight: 700, flexShrink: 0 }}>Top Result</span>}
                      </div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: r.top ? "#a5b4fc" : "#c7d2fe", lineHeight: 1.3, marginBottom: 2 }}>{r.title}</div>
                      <div style={{ fontSize: 10, color: "#475569", lineHeight: 1.4 }}>{r.desc}</div>
                    </div>
                  ))}
                </div>

                {/* keyword bars */}
                <div style={{ padding: "6px 14px 12px", borderTop: "1px solid rgba(255,255,255,.04)" }}>
                  <div style={{ fontSize: 10, color: "#4b5563", marginBottom: 8 }}>Top Ranking Keywords</div>
                  {[["mobile app dev", 92],["react native co.", 78],["shopify experts", 65]].map(([kw, pct], i) => (
                    <div key={i} style={{ marginBottom: 5 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#64748b", marginBottom: 2 }}>
                        <span>{kw}</span><span style={{ color: "#8fa3f8" }}>{pct}%</span>
                      </div>
                      <div style={{ height: 4, borderRadius: 99, background: "rgba(255,255,255,.06)", overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${pct}%`, borderRadius: 99, background: "linear-gradient(90deg,#2d3fb5,#4f8ef7)" }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── FLOATING BADGE 1: Google Rank #1 ── */}
              <div
                className="seo-badge-rank"
                style={{
                  position: "absolute", top: 0, right: -12, zIndex: 30,
                  background: "rgba(8,10,26,.97)", border: "1px solid rgba(74,95,212,.3)",
                  borderRadius: 14, padding: "12px 18px", backdropFilter: "blur(20px)",
                  textAlign: "center", minWidth: 112,
                  boxShadow: "0 14px 40px rgba(0,0,0,.55)",
                  animation: "seoFadeUp .7s ease 1s both, seoFloat 7s ease-in-out 1.5s infinite",
                }}
              >
                <div style={{ fontSize: 10, color: "#4b5563", marginBottom: 4 }}>Google Rank</div>
                <div style={{ fontSize: 28, fontWeight: 900, color: "#fff", lineHeight: 1, fontFamily: "'Outfit',sans-serif" }}>#1 🏆</div>
              </div>

              {/* ── FLOATING BADGE 2: Traffic Growth ── */}
              <div
                className="seo-badge-traffic"
                style={{
                  position: "absolute", bottom: 20, right: -12, zIndex: 30,
                  background: "rgba(8,10,26,.97)", border: "1px solid rgba(74,222,128,.22)",
                  borderRadius: 14, padding: "12px 16px", backdropFilter: "blur(20px)",
                  boxShadow: "0 14px 40px rgba(0,0,0,.55)",
                  animation: "seoFadeUp .7s ease 1.2s both, seoFloatR 9s ease-in-out 2s infinite",
                }}
              >
                <div style={{ fontSize: 10, color: "#4b5563", marginBottom: 4 }}>Traffic Growth</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: "#4ade80", fontFamily: "'Outfit',sans-serif" }}>+324%</div>
                <div style={{ fontSize: 9, color: "#4b5563", marginTop: 2 }}>12 months</div>
              </div>

              {/* ── FLOATING BADGE 3: Page 1 keywords ── */}
              <div
                className="seo-badge-kw"
                style={{
                  position: "absolute", bottom: 72, left: -10, zIndex: 30,
                  background: "rgba(8,10,26,.97)", border: "1px solid rgba(74,95,212,.28)",
                  borderRadius: 14, padding: "12px 16px", backdropFilter: "blur(20px)",
                  boxShadow: "0 14px 40px rgba(0,0,0,.55)",
                  animation: "seoFadeUp .7s ease 1.4s both, seoFloat 8s ease-in-out 2.5s infinite",
                }}
              >
                <div style={{ fontSize: 10, color: "#4b5563", marginBottom: 4 }}>Page 1 Keywords</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: "#8fa3f8", fontFamily: "'Outfit',sans-serif" }}>847+</div>
                <div style={{ fontSize: 9, color: "#4ade80", marginTop: 2 }}>↑ from 12 last year</div>
              </div>

            </div>
          </div>

          {/* ── STATS STRIP ── */}
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(4,1fr)",
            gap: 1, background: "rgba(74,95,212,.1)",
            borderRadius: 18, overflow: "hidden",
            border: "1px solid rgba(74,95,212,.18)",
            marginTop: 56,
          }}>
            {[["340%","Avg Traffic Increase"],["12mo","Avg Time to Page 1"],["847+","Page 1 Keywords"],["99%","Client Retention"]].map(([val,lbl],i) => (
              <div key={i}
                style={{ padding: "24px 24px", background: "rgba(8,11,28,.9)", borderRight: i<3?"1px solid rgba(255,255,255,.04)":"none", transition: "background .3s", cursor: "default" }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(45,63,181,.1)"}
                onMouseLeave={e => e.currentTarget.style.background = "rgba(8,11,28,.9)"}
              >
                <div style={{ fontSize: 28, fontWeight: 900, background: "linear-gradient(135deg,#8fa3f8,#2d3fb5)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent", fontFamily: "'Outfit',sans-serif" }}>{val}</div>
                <div style={{ color: "#374151", fontSize: 12, marginTop: 6 }}>{lbl}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          ALL REMAINING SECTIONS — unchanged
      ════════════════════════════════════════ */}

      {/* SERVICES */}
      <section className="py-28 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-3xl mx-auto">
            <div className="text-indigo-400 uppercase tracking-[0.2em] text-sm font-semibold">SEO SERVICES</div>
            <h2 className="text-5xl font-black mt-6 leading-tight">
              Enterprise SEO Services
              <span className="block text-indigo-400">Built For Growth</span>
            </h2>
            <p className="text-slate-400 leading-8 mt-8">
              We provide technical SEO, content optimization, authority building, local SEO, and scalable organic growth systems for modern businesses.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-20">
            {services.map((service, i) => (
              <div key={i} className="group bg-[#0E142B] border border-white/10 rounded-3xl p-8 hover:border-indigo-500/30 hover:-translate-y-2 transition-all duration-500">
                <div className="w-14 h-14 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400 text-2xl mb-6">→</div>
                <h3 className="text-2xl font-bold mb-4 group-hover:text-indigo-300 transition">{service.title}</h3>
                <p className="text-slate-400 leading-8">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="py-28 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <div className="text-indigo-400 uppercase tracking-[0.2em] text-sm font-semibold">INDUSTRY SEO</div>
            <h2 className="text-5xl font-black mt-6 leading-tight">
              SEO Services For
              <span className="block text-indigo-400">High-Growth Industries</span>
            </h2>
            <p className="text-slate-400 leading-8 mt-8">
              Specialized SEO services for SaaS platforms, ERP software companies, manufacturers, eCommerce brands, and startups.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
            {industries.map((item, i) => (
              <div key={i} className="bg-[#0E142B] border border-white/10 rounded-2xl p-6 hover:border-indigo-500/30 transition-all duration-300">
                <h3 className="font-bold text-lg leading-8">{item}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-28 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-20">
            <div className="text-indigo-400 uppercase tracking-[0.2em] text-sm font-semibold">SEO PROCESS</div>
            <h2 className="text-5xl font-black mt-6">Our SEO Growth System</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {process.map((item, i) => (
              <div key={i} className="bg-[#0E142B] border border-white/10 rounded-3xl p-8 hover:border-indigo-500/30 transition-all duration-300">
                <div className="text-5xl font-black text-indigo-400/30 mb-6">0{i + 1}</div>
                <h3 className="text-2xl font-bold">{item}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SEO CONTENT */}
      <section className="py-28 border-b border-white/5">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <h2 className="text-5xl font-black mb-10">Enterprise SEO Services in India</h2>
          <div className="space-y-8 text-slate-400 text-lg leading-9">
            <p>SoftCoders provides enterprise SEO services in India focused on technical optimization, semantic content systems, authority building, and scalable organic growth.</p>
            <p>Our SEO specialists help SaaS companies, ERP software providers, manufacturers, local businesses, startups, and eCommerce brands improve rankings and generate high-quality leads.</p>
            <p>We optimize Core Web Vitals, schema markup, keyword intent mapping, internal linking systems, and semantic content clusters to improve search visibility and ranking performance.</p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-28 border-b border-white/5">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-20">
            <div className="text-indigo-400 uppercase tracking-[0.2em] text-sm font-semibold">FAQ</div>
            <h2 className="text-5xl font-black mt-6">SEO Questions Answered</h2>
          </div>
          <div className="space-y-6">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-[#0E142B] border border-white/10 rounded-3xl p-8">
                <h3 className="text-2xl font-bold">{faq.q}</h3>
                <p className="text-slate-400 leading-8 mt-4">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      

    </div>
  );
}